import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Routes } from 'config';
import { mapQuery } from 'utils';
import { Details } from 'components';
import { useAppSelector } from 'store/hooks';
import { useListQuery } from 'store/api/children';
import { selectAuthUser } from 'store/slices/auth';

import { TabsNav } from './tabs-nav';

const { Home, Edit, Create } = Routes.FamilyInformation;
const createPath = `${Home}/${Create}`;

export default function Layout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation(['familyInformation']);
  const authUser = useAppSelector(selectAuthUser);
  const childrenSetupRequired = authUser?.childrenSetupRequiredCount !== 0;

  const { data, ...props } = useListQuery({ limit: 100 });

  useEffect(() => {
    if (pathname === Home && !props.isLoading) {
      if (data && !!data?.data?.length) {
        let childId = data?.data?.[0]?.id;

        if (childrenSetupRequired) {
          const firstRequiredChildId = data?.data?.find(
            (c) => c.isSetupRequired
          )?.id;
          if (firstRequiredChildId) {
            childId = firstRequiredChildId;
          }
        }
        navigate(`${Home}/${Edit.replace(':id', String(childId))}`, {
          replace: true,
        });
        return;
      }

      navigate(createPath, { replace: true });
    }
  }, [data, pathname, navigate, props.isLoading, childrenSetupRequired]);

  const getTabsValue = () => {
    const activeTab = pathname?.split('/')?.[2];

    if (pathname === createPath) {
      return 'create';
    }

    if (data?.data?.some((child) => child.id === activeTab)) {
      return activeTab;
    }

    return false;
  };

  const onTabChange = (id: string) => navigate(Edit.replace(':id', id));

  return (
    <Details {...mapQuery(props)}>
      <div className="flex flex-col gap-4 md:gap-6">
        <h1 className="text-lg md:text-2xl">{t('title')}</h1>
        <div className="shadow-greendark-shadow8 border-gray-primary -mx-4 border-[1.5px] bg-white p-4 shadow-[0px_16px_32px_0px] md:mx-0 md:rounded-lg md:p-6">
          <p>{t('description')}</p>
          <div className="mt-4 flex flex-col gap-4 md:gap-6 lg:flex-row">
            <div className="border-gray-primary flex-shrink-0 border-b lg:w-74 lg:border-r lg:border-b-0">
              <TabsNav
                data={data?.data}
                value={getTabsValue()}
                onChange={onTabChange}
                onAddChild={() => {
                  navigate(createPath, { replace: true });
                }}
              />
            </div>
            <div className="lg:flex-1">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </Details>
  );
}
